/**
 * @file CategoriesView.tsx
 *
 * Categories tab screen. Fetches the full category list, renders it as a
 * searchable, paginated, tree-ordered table ({@link CategoriesTable} /
 * {@link Pagination}), and drives create/edit (routed) and delete
 * (confirmed inline). Columns collapse on smaller viewports via the
 * {@link useMediaQuery} hook, and the screen renders loading / empty /
 * no-results / error states.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { notifyError, notifySuccess } from '../feedback/toast';
import { useMediaQuery } from '../hooks/use-media-query';
import { useCategoriesStore } from '../stores/categories.store';
import type { ShopCategory } from '../api/shop-types';
import { EmptyState } from '../components/EmptyState';
import { ConfirmModal } from '../components/ConfirmModal';
import { buildCategoryRows, CATEGORIES_PAGE_SIZE } from './category-list';
import { CategoriesTable } from './CategoriesTable';
import { Pagination } from '../components/Pagination';
import shared from '../shared.module.css';
import styles from './CategoriesView.module.css';

/**
 * Renders the Categories tab.
 *
 * @returns The categories screen.
 */
export function CategoriesView() {
  const intl = useIntl();
  const navigate = useNavigate();

  const categories = useCategoriesStore((s) => s.categories);
  const loading = useCategoriesStore((s) => s.loading);
  const error = useCategoriesStore((s) => s.error);
  const fetchCategories = useCategoriesStore((s) => s.fetchCategories);
  const deleteCategory = useCategoriesStore((s) => s.deleteCategory);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState<ShopCategory | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const isMobile = useMediaQuery('Mobile');
  const isDesktop = useMediaQuery('Desktop');
  const showImage = !isMobile;
  const showDescription = isDesktop;
  const showSlug = isDesktop;

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const rows = useMemo(() => {
    const all = buildCategoryRows(categories);
    const term = debouncedSearch.trim().toLowerCase();
    return term ? all.filter((r) => r.category.name.toLowerCase().includes(term)) : all;
  }, [categories, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(rows.length / CATEGORIES_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice(
    (safePage - 1) * CATEGORIES_PAGE_SIZE,
    safePage * CATEGORIES_PAGE_SIZE,
  );

  const handleDelete = async (id: string): Promise<void> => {
    setDeleting(id);
    setDeleteError(null);
    const ok = await deleteCategory(id);
    setDeleting(null);
    if (!ok) {
      const code = useCategoriesStore.getState().formErrorCode;
      setDeleteError(
        intl.formatMessage(
          code === 'category_has_children'
            ? adminMessages.categoriesDeleteHasChildren
            : adminMessages.categoriesDeleteError,
        ),
      );
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.toastDeleted));
    setConfirmDelete(null);
    await fetchCategories();
  };

  const openCreate = (): void => {
    navigate('/categories/new');
  };
  const openEdit = (category: ShopCategory): void => {
    navigate(`/categories/${category.id}/edit`);
  };
  const askDelete = (category: ShopCategory): void => {
    setDeleteError(null);
    setConfirmDelete(category);
  };

  if (loading && categories.length === 0) {
    return (
      <div className="gv-flex gv-items-center gv-justify-center gv-h-full gv-w-full">
        <Loader>
          <FormattedMessage {...adminMessages.categoriesLoading} />
        </Loader>
      </div>
    );
  }

  if (error && categories.length === 0) {
    return (
      <EmptyState
        testId="categories-error"
        title={intl.formatMessage(adminMessages.categoriesErrorTitle)}
        description={intl.formatMessage(adminMessages.categoriesErrorDescription)}
        action={{
          label: intl.formatMessage(adminMessages.categoriesTitle),
          onClick: () => void fetchCategories(),
        }}
      />
    );
  }

  return (
    <div className={`${styles.container} ${shared.outerContainer}`}>
      <div className={styles.toolbar}>
        <h2 className={`gv-heading-lg ${styles.title}`}>
          <FormattedMessage {...adminMessages.categoriesTitle} />
        </h2>
        <button
          className="gv-button gv-button-primary"
          type="button"
          data-testid="categories-add-button"
          onClick={openCreate}
        >
          <Icon name="add" size="small" />
          <FormattedMessage {...adminMessages.categoriesAdd} />
        </button>
      </div>

      <div className="gv-flex gv-flex-col gv-gap-md">
        {categories.length > 0 && (
          <div className="gv-flex gv-justify-end">
            <div className="gv-mode-condensed">
              <div className={`gv-input gv-input-search gv-surface-bright ${styles.searchField}`}>
                <input
                  type="search"
                  data-testid="categories-search"
                  value={search}
                  placeholder={intl.formatMessage(adminMessages.categoriesSearchPlaceholder)}
                  aria-label={intl.formatMessage(adminMessages.categoriesSearchLabel)}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <span className="gv-addon" aria-hidden="true">
                  <Icon name="search" size="small" />
                </span>
              </div>
            </div>
          </div>
        )}

        {categories.length === 0 ? (
          <EmptyState
            testId="categories-empty"
            title={intl.formatMessage(adminMessages.categoriesEmptyTitle)}
            description={intl.formatMessage(adminMessages.categoriesEmptyDescription)}
            action={{
              label: intl.formatMessage(adminMessages.categoriesAdd),
              onClick: openCreate,
            }}
            labelPrefix={<Icon name="add" size="small" />}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            testId="categories-no-results"
            title={intl.formatMessage(adminMessages.categoriesNoResultsTitle)}
            description={intl.formatMessage(adminMessages.categoriesNoResultsDescription)}
          />
        ) : (
          <div className="gv-data-table">
            <CategoriesTable
              rows={pageRows}
              showImage={showImage}
              showDescription={showDescription}
              showSlug={showSlug}
              onEdit={openEdit}
              onDelete={askDelete}
            />
            <Pagination
              page={safePage}
              totalPages={totalPages}
              onPage={setPage}
              navLabel={intl.formatMessage(adminMessages.categoriesTitle)}
              testIdPrefix="categories-pagination"
            />
          </div>
        )}
      </div>

      {confirmDelete && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.categoriesDeleteTitle)}
          message={intl.formatMessage(adminMessages.categoriesDeleteMessage, {
            name: confirmDelete.name,
          })}
          confirmLabel={intl.formatMessage(adminMessages.categoriesDelete)}
          cancelLabel={intl.formatMessage(adminMessages.categoriesCancel)}
          loadingLabel={intl.formatMessage(adminMessages.categoriesDeleteLoading)}
          confirmVariant="destructive"
          loading={deleting === confirmDelete.id}
          errorMessage={deleteError ?? undefined}
          onPrimaryClick={() => void handleDelete(confirmDelete.id)}
          onSecondaryClick={() => setConfirmDelete(null)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
